<template>
  <v-card :loading="loading" outlined class="comment-card">
    <v-card-text class="pa-4">
      <div class="comment-header">
        <div>
          <div class="section-title">{{ $t("pageHeader.comment") }}</div>
          <div class="section-subtitle">
            {{ comments.length }} comment{{ comments.length === 1 ? "" : "s" }}
          </div>
        </div>
      </div>

      <div class="comment-compose mt-4">
        <v-avatar size="42">
          <img
            v-if="currentUser.imageUrl"
            :src="currentUser.imageUrl"
            alt="User"
          />
          <img v-else src="@/assets/images/user.jpeg" alt="User" />
        </v-avatar>

        <div class="comment-input-wrap">
          <v-text-field
            v-model="comment"
            label="Comment"
            placeholder="Add a note about this vendor"
            outlined
            dense
            hide-details
            @keyup.enter="saveComment"
          />

          <div v-if="comment" class="comment-actions">
            <v-btn small text @click="cancel">
              Cancel
            </v-btn>

            <v-btn
              small
              rounded
              color="primary"
              :loading="saving"
              dark
              @click="saveComment"
            >
              Comment
            </v-btn>
          </div>
        </div>
      </div>

      <v-divider class="my-4" />

      <div class="comment-toolbar">
        <v-text-field
          v-model="search"
          outlined
          prepend-inner-icon="search"
          dense
          label="Search comments"
          single-line
          hide-details
          clearable
          class="comment-search"
        />
      </div>

      <v-skeleton-loader
        v-if="loading"
        type="list-item-two-line, list-item-two-line, list-item-two-line"
        class="mt-4"
      />

      <v-data-table
        v-else
        :mobile-breakpoint="0"
        :headers="headers"
        :items="comments"
        :search="search"
        :loading="loading"
        :sort-by="sortBy"
        :page.sync="page"
        :items-per-page="itemsPerPage"
        class="comment-table mt-4"
      >
        <template v-slot:item="{ item }">
          <tr class="comment-row">
            <td class="text-left">
              {{ item.createdAt | moment("MM/DD/YYYY hh:mm A") }}
            </td>

            <td class="text-left">
              {{ item.User ? item.User.name : "N/A" }}
            </td>

            <td class="text-left">
              {{ item.message }}
            </td>
          </tr>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">
          Your search for "{{ search }}" found no results.
        </v-alert>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import commentService from "../service.js";
import { mapGetters } from "vuex";

export default {
  name: "vendor-comments",

  props: ["RelationId", "TypeId"],

  data() {
    return {
      comment: null,
      comments: [],
      sortBy: "createdAt",
      pageCount: 0,
      page: 1,
      itemsPerPage: 10,
      loading: true,
      saving: false,
      headers: [
        {
          text: "Date",
          align: "left",
          value: "createdAt",
          width: "20%",
          sortable: true,
        },
        {
          text: "User",
          align: "left",
          value: "User.name",
          width: "20%",
          sortable: true,
        },
        {
          text: "Message",
          align: "left",
          value: "message",
          sortable: true,
        },
      ],
      search: null,
    };
  },

  computed: {
    ...mapGetters("global", ["currentUser"]),
  },

  watch: {
    RelationId: {
      immediate: true,
      handler() {
        this.getList();
      },
    },
  },

  methods: {
    cancel() {
      this.comment = null;
    },

    save(data) {
      this.saving = true;

      return commentService
        .create(data)
        .then(() => {
          this.comment = null;
          this.getList();
        })
        .finally(() => {
          this.saving = false;
        });
    },

    saveComment() {
      if (!this.comment || !this.RelationId || !this.RelationId.id) {
        return;
      }

      this.save({
        RelationId: this.RelationId.id,
        message: this.comment,
        TypeId: this.TypeId,
      });
    },

    getList() {
      if (!this.RelationId || !this.RelationId.id) {
        this.comments = [];
        this.loading = false;
        return;
      }

      this.loading = true;

      return commentService
        .getAll({
          RelationId: this.RelationId.id,
          TypeId: this.TypeId,
        })
        .then((response) => {
          this.comments = response.data || [];
          return response;
        })
        .catch((error) => {
          console.error("comments getList failed", error);
          this.comments = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.comment-card {
  border-radius: 20px;
  background: #ffffff;
}

.comment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.section-title {
  font-size: 18px;
  font-weight: 800;
  color: #0f172a;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.comment-compose {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.comment-input-wrap {
  flex: 1;
  min-width: 0;
}

.comment-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 10px;
}

.comment-toolbar {
  display: flex;
  align-items: center;
}

.comment-search {
  max-width: 340px;
}

.comment-table {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
}

.comment-row:hover {
  background: #f8fafc;
}

@media (max-width: 600px) {
  .comment-compose {
    align-items: flex-start;
  }

  .comment-search {
    max-width: 100%;
  }
}
</style>